import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../api/axiosInstance";

const formatDate = (value) => {
  if (!value) return "";

  const d = new Date(value);
  if (isNaN(d.getTime())) return value; // agar date nahi hai to as-is

  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();

  return `${day}-${month}-${year}`; // DD-MM-YYYY
};

function WagesCalculator() {
  const navigate = useNavigate();

  /* =========================
     MONTH + YEAR STATE
  ========================= */
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); // 1–12

  // Backend compatible YYYY-MM
  const selectedPeriod = `${year}-${String(month).padStart(2, "0")}`;

  /* =========================
     DATA STATE
  ========================= */
  const [loading, setLoading] = useState(false);

  const [kpi, setKpi] = useState({
    totalAmount: 0,
    completedLotAmount: 0,
    wipAmount: 0,
  });

  const [summaryRows, setSummaryRows] = useState([]);
  const [lotRows, setLotRows] = useState([]);

  /* =========================
     PAGINATION
  ========================= */
  const rowsPerPage = 10;
  const [summaryPage, setSummaryPage] = useState(1);
  const [lotPage, setLotPage] = useState(1);

  /* =========================
     FETCH DATA
  ========================= */
  const fetchData = async () => {
    try {
      setLoading(true);

      const [kpiRes, summaryRes, lotRes] = await Promise.all([
        axios.get(`/wages/summary?month=${selectedPeriod}`),
        axios.get(`/wages/monthly?month=${selectedPeriod}`),
        axios.get(`/wages/monthly-lotwise?month=${selectedPeriod}`),
      ]);

      setKpi(kpiRes.data || {});
      setSummaryRows(Array.isArray(summaryRes.data) ? summaryRes.data : []);
      setLotRows(Array.isArray(lotRes.data) ? lotRes.data : []);

      setSummaryPage(1);
      setLotPage(1);
    } catch (err) {
      console.error("Wages fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);

  /* =========================
     HELPERS
  ========================= */
  const paginate = (rows, page) =>
    rows.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const summaryPageRows = paginate(summaryRows, summaryPage);
  const lotPageRows = paginate(lotRows, lotPage);

  const summaryColumns = useMemo(
    () => (summaryRows[0] ? Object.keys(summaryRows[0]) : []),
    [summaryRows]
  );

  const lotColumns = useMemo(
    () => (lotRows[0] ? Object.keys(lotRows[0]) : []),
    [lotRows]
  );

  /* =========================
     EXPORT (FIXED - BLOB DOWNLOAD)
  ========================= */
  const exportExcel = async (type) => {
    try {
      const response = await axios.get(
        `/wages/export/${type}?month=${selectedPeriod}`,
        {
          responseType: "blob", // 🔥 IMPORTANT
        }
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Wages_${type}_${selectedPeriod}.xlsx`;

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Excel export failed:", err);
      alert("Excel export failed. Please try again.");
    }
  };

  /* =========================
     UI
  ========================= */
  return (
    <div className="p-4">
      {/* HEADER */}
      <div className="mb-4 flex justify-between items-center">
        <button
          onClick={() => navigate(-1)}
          className="px-3 py-1 bg-gray-700 text-white rounded hover:bg-gray-800 text-sm"
        >
          ⬅️ Back
        </button>

        {/* MONTH DROPDOWN + YEAR SWITCH */}
        <div className="flex items-center gap-4 bg-white border rounded px-4 py-2 shadow-sm">
          {/* YEAR SWITCH */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setYear((y) => y - 1)}
              className="px-2 py-1 border rounded hover:bg-gray-100"
            >
              ◀
            </button>

            <span className="font-semibold text-lg">{year}</span>

            <button
              onClick={() => setYear((y) => y + 1)}
              className="px-2 py-1 border rounded hover:bg-gray-100"
            >
              ▶
            </button>
          </div>

          {/* MONTH DROPDOWN */}
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="border rounded px-3 py-1 text-sm"
          >
            <option value={1}>January</option>
            <option value={2}>February</option>
            <option value={3}>March</option>
            <option value={4}>April</option>
            <option value={5}>May</option>
            <option value={6}>June</option>
            <option value={7}>July</option>
            <option value={8}>August</option>
            <option value={9}>September</option>
            <option value={10}>October</option>
            <option value={11}>November</option>
            <option value={12}>December</option>
          </select>
        </div>
      </div>

      {/* KPI TILES */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <KpiTile label="Total Amount" value={kpi.totalAmount} />
        <KpiTile
          label="Completed Lot Amount"
          value={kpi.completedLotAmount}
        />
        <KpiTile label="WIP Amount" value={kpi.wipAmount} />
      </div>

      {/* SUMMARY TABLE */}
      <TableSection
        title="Worker Wages Monthly (Summary)"
        columns={summaryColumns}
        rows={summaryPageRows}
        loading={loading}
        page={summaryPage}
        setPage={setSummaryPage}
        total={summaryRows.length}
        exportFn={() => exportExcel("monthly")}
      />

      {/* LOT-WISE TABLE */}
      <TableSection
        title="Worker Wages Monthly (Lot Wise)"
        columns={lotColumns}
        rows={lotPageRows}
        loading={loading}
        page={lotPage}
        setPage={setLotPage}
        total={lotRows.length}
        exportFn={() => exportExcel("monthly-lotwise")}
      />
    </div>
  );
}

/* =========================
   KPI TILE
========================= */
const KpiTile = ({ label, value }) => (
  <div className="bg-white border rounded shadow p-4">
    <div className="text-sm text-gray-500">{label}</div>
    <div className="text-xl font-semibold mt-1">
      ₹ {Number(value || 0).toLocaleString("en-IN")}
    </div>
  </div>
);

/* =========================
   TABLE SECTION
========================= */
const TableSection = ({
  title,
  columns,
  rows,
  loading,
  page,
  setPage,
  total,
  exportFn,
}) => {
  const totalPages = Math.max(1, Math.ceil(total / 10));

  return (
    <div className="mb-8">
      <div className="mb-2 flex justify-between items-center">
        <h2 className="font-semibold">{title}</h2>
        <button
          onClick={exportFn}
          className="px-3 py-1 bg-green-600 text-white rounded text-sm"
        >
          Export Excel
        </button>
      </div>

      <div className="overflow-auto border rounded shadow">
        {loading ? (
          <div className="p-4 text-sm text-gray-500">Loading…</div>
        ) : (
          <table className="min-w-[900px] w-full text-sm border-collapse">
            <thead className="bg-gray-100">
              <tr>
                {columns.map((col) => (
                  <th
                    key={col}
                    className="border px-2 py-2 text-left whitespace-nowrap"
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length || 1}
                    className="border px-2 py-3 text-center text-gray-500"
                  >
                    No data
                  </td>
                </tr>
              ) : (
                rows.map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    {columns.map((col) => (
                      <td className="border px-2 py-1 whitespace-nowrap">
                        {[
                          "date",
                          "period",
                          "created",
                          "createdon",
                          "created_on",
                        ].some((k) => col.toLowerCase().includes(k))
                          ? formatDate(row[col])
                          : String(row[col] ?? "")
                        }
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* PAGINATION */}
      <div className="mt-2 flex justify-between items-center text-sm">
        <span>
          Page {page} of {totalPages}
        </span>
        <div className="space-x-2">
          <button
            onClick={() => setPage((p) => Math.max(p - 1, 1))}
            disabled={page === 1}
            className="px-2 py-1 bg-gray-200 rounded disabled:opacity-50"
          >
            Prev
          </button>
          <button
            onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
            disabled={page === totalPages}
            className="px-2 py-1 bg-gray-200 rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default WagesCalculator;
